import React from 'react';
import styled from 'styled-components';
import { usePublicStats } from '../hooks/usePublicStats';
import { usePulseXPrice } from '../hooks/usePulseXPrice';
import { CONTRACTS } from '../utils/contracts';
import { formatNumber, formatCurrency } from '../utils/formatters';
import { Card, StatGrid, StatCard, WalletButton, Alert } from './StyledComponents';

// ---------------------------------------------------------------------------
// Pre-connect landing page. Everything shown here is read live from PulseChain
// over public RPCs — no wallet, no server, no tracking. The goal: answer
// "what is this / is it real / can I trust it / how does it work" BEFORE
// asking anyone to connect anything.
// ---------------------------------------------------------------------------

const Landing = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Hero = styled.div`
  text-align: center;
  padding: 3rem 1rem 1rem;

  h2 {
    font-size: 2.6rem;
    line-height: 1.15;
    margin: 1rem 0;
    color: #fff;

    .gold {
      background: linear-gradient(45deg, #ffd700, #ffed4a);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    @media (max-width: 768px) {
      font-size: 1.9rem;
    }
  }

  .lede {
    font-size: 1.1rem;
    color: #ccc;
    max-width: 640px;
    margin: 0 auto 2rem;
    line-height: 1.6;
  }

  .fineprint {
    display: block;
    margin-top: 0.9rem;
    font-size: 0.8rem;
    color: #777;
  }
`;

const LivePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.4);
  color: #10b981;
  border-radius: 999px;
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;

  &::before {
    content: '●';
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    50% { opacity: 0.35; }
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1.5rem;
  font-size: 1.3rem;
  color: #ffd700;
  text-align: center;
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  background: rgba(16, 16, 16, 0.5);
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 1.5rem;

  .num {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    line-height: 2rem;
    text-align: center;
    border-radius: 50%;
    background: linear-gradient(45deg, #ffd700, #f59e0b);
    color: #000;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }

  h4 {
    margin: 0 0 0.5rem;
    color: #fff;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: #aaa;
    line-height: 1.5;
  }
`;

const CapacityTrack = styled.div`
  height: 10px;
  background: #2a2a2a;
  border-radius: 5px;
  overflow: hidden;
  margin: 0.75rem 0 0.4rem;

  .fill {
    height: 100%;
    min-width: 4px;
    background: linear-gradient(90deg, #ffd700, #f59e0b);
    width: ${props => Math.min(100, Math.max(0.5, props.$pct))}%;
    transition: width 0.5s ease;
  }
`;

const VerifyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;

  a {
    color: #ffd700;
    text-decoration: none;
    border: 1px solid #444;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ffd700;
      background: rgba(255, 215, 0, 0.06);
    }
  }
`;

const dash = '—';

const LandingPage = ({ onConnect, isConnecting, connected, onEnterVault }) => {
  const stats = usePublicStats();
  const { jdaiMarketPrice, marketPlsPrice } = usePulseXPrice();

  const s = (v, fmt) => (stats.isLoading ? '…' : fmt(v));
  const minColl = stats.isLoading ? 150 : stats.minCollateralPct;
  const maxBorrowPct = Math.floor(10000 / minColl); // e.g. 150% -> 66%
  const ceilingPct = stats.debtCeiling > 0 ? (stats.totalDebt / stats.debtCeiling) * 100 : 0;

  return (
    <Landing>
      {/* ------------------------------ Hero ------------------------------ */}
      <Hero>
        <LivePill>
          Live on PulseChain {stats.isLoading ? '' :
            `— ${formatNumber(stats.plsLocked, 0)} PLS in vaults right now`}
        </LivePill>
        <h2>
          Borrow against your PLS.<br />
          <span className="gold">Priced in gold.</span>
        </h2>
        <p className="lede">
          JDAI is a vault system on PulseChain, adapted from MakerDAO&apos;s open-source
          vault engine. Deposit PLS into a vault that stays under your address, mint
          JDAI — a token targeting <strong style={{ color: '#ffd700' }}>1/1000th of an
          ounce of gold</strong> — and repay whenever you like to unlock every PLS you
          put in. Borrowing takes a single transaction. No sign-ups, no
          deadlines{stats.stabilityFee !== null &&
          parseFloat(stats.stabilityFee) === 0 ? ', and no interest charged today' : ''}.
        </p>
        <WalletButton onClick={connected ? onEnterVault : onConnect} disabled={isConnecting}>
          {connected ? 'Go to your vault →' : isConnecting ? 'Connecting...' : 'Connect Wallet to Open a Vault'}
        </WalletButton>
        {!connected && (
          <span className="fineprint">
            Connecting only shares your address. Every action after that is a separate
            transaction you approve — and everything below is visible without connecting.
          </span>
        )}
      </Hero>

      {/* --------------------------- Live stats --------------------------- */}
      <Card>
        <SectionTitle>The system, live from the chain</SectionTitle>
        <StatGrid>
          <StatCard>
            <div className="label">PLS Locked</div>
            <div className="value">{s(stats.plsLocked, v => formatNumber(v, 0))}</div>
          </StatCard>
          <StatCard>
            <div className="label">JDAI in Circulation</div>
            <div className="value">{s(stats.jdaiSupply, v => formatNumber(v, 2))}</div>
          </StatCard>
          <StatCard>
            <div className="label">Gold Target (1/1000 oz)</div>
            <div className="value">{s(stats.goldTarget, v => formatCurrency(v, 4))}</div>
          </StatCard>
          <StatCard>
            <div className="label">Market Price (PulseX)</div>
            <div className="value">
              {jdaiMarketPrice > 0 ? formatCurrency(jdaiMarketPrice, 4) : dash}
            </div>
          </StatCard>
          <StatCard type="success">
            <div className="label">Stability Fee</div>
            <div className="value">
              {stats.stabilityFee === null ? dash : `${stats.stabilityFee}% APY`}
            </div>
          </StatCard>
          <StatCard>
            <div className="label">Min Collateral</div>
            <div className="value">{s(stats.minCollateralPct, v => `${formatNumber(v, 0)}%`)}</div>
          </StatCard>
        </StatGrid>

        {/* Capacity, framed as room to grow — same fact as "utilization" */}
        <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span>
              Minted: <strong style={{ color: '#ffd700' }}>
                {s(stats.totalDebt, v => formatNumber(v, 0))} JDAI
              </strong> of a {s(stats.debtCeiling, v => formatNumber(v, 0))} JDAI ceiling
            </span>
            <span style={{ color: '#10b981' }}>
              {s(stats.mintable, v => formatNumber(v, 0))} JDAI mintable today
            </span>
          </div>
          <CapacityTrack $pct={ceilingPct}><div className="fill" /></CapacityTrack>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#777', margin: '1rem 0 0', textAlign: 'center' }}>
          System oracle: 1 PLS = {s(stats.oraclePlsPrice, v => formatCurrency(v, 9))}
          {marketPlsPrice > 0 && <> · PulseX market: 1 PLS = {formatCurrency(marketPlsPrice, 9)}</>}
          {' '}· refreshes every minute from public RPCs
        </p>
      </Card>

      {/* --------------------------- How it works ------------------------- */}
      <Card>
        <SectionTitle>How it works</SectionTitle>
        <StepGrid>
          <Step>
            <div className="num">1</div>
            <h4>Deposit PLS</h4>
            <p>
              Your PLS goes into a vault tied to your own address. It isn&apos;t lent
              out, staked, or pooled — it sits as collateral until you take it back.
            </p>
          </Step>
          <Step>
            <div className="num">2</div>
            <h4>Mint JDAI</h4>
            <p>
              Mint up to ~{maxBorrowPct}% of your collateral&apos;s value as new JDAI —
              the {formatNumber(minColl, 0)}% minimum keeps every JDAI over-backed.
              Vaults start at just {s(stats.minDebt, v => formatNumber(v, 0))} JDAI.
            </p>
          </Step>
          <Step>
            <div className="num">3</div>
            <h4>Repay &amp; withdraw</h4>
            <p>
              Return the JDAI whenever you like — no schedule, no deadline — and your
              PLS unlocks. Sell it, spend it, or hold it in gold terms meanwhile.
            </p>
          </Step>
        </StepGrid>
      </Card>

      {/* ------------------------- Why people use it ---------------------- */}
      <Card>
        <SectionTitle>Why gold-denominated?</SectionTitle>
        <StepGrid>
          <Step>
            <h4>Keep your upside</h4>
            <p>
              Borrowing against PLS instead of selling it means every coin comes back
              to you on repayment — you never exit your position to get liquidity.
            </p>
          </Step>
          <Step>
            <h4>Think in gold</h4>
            <p>
              Each JDAI targets 1/1000 oz of gold&apos;s USD value — a unit of account
              older than any currency, tracked on-chain by the system oracle.
            </p>
          </Step>
          <Step>
            <h4>Simple, visible terms</h4>
            <p>
              {stats.stabilityFee !== null && parseFloat(stats.stabilityFee) === 0
                ? 'A 0% stability fee today, and no' : 'No'} repayment schedule — but the
              {' '}{formatNumber(minColl, 0)}% minimum is a real line: a vault below it
              can&apos;t borrow or withdraw, and once liquidations are switched on
              (coming, announced in advance) it can be liquidated with a penalty.
            </p>
          </Step>
        </StepGrid>
      </Card>

      {/* ------------------------- Honest disclosure ---------------------- */}
      <Alert type="info">
        <div className="title">Read this first — it&apos;s called an unstablecoin for a reason</div>
        <div className="message">
          <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <li>
              <strong>Keep your vault above {formatNumber(minColl, 0)}% — liquidations are
              real.</strong> A vault below the line can&apos;t mint or withdraw, and the
              liquidation system (installed, being activated in stages with advance
              notice) will auction undercollateralized vaults with a 13% penalty.
              Watching your own vault is your job.
            </li>
            <li>
              <strong>This is a minimal, early-stage deployment.</strong> The core MakerDAO
              vault engine is live — ledger, oracle, vault mechanics. The liquidation and
              auction layers are installed but deliberately inactive (see the Contracts
              tab); the savings and governance layers are not deployed. Small on purpose
              while it proves itself.
            </li>
            <li>
              <strong>One key administers it.</strong> System parameters (price feed,
              ceiling, fees) are set by the deployer&apos;s address, not a DAO. Don&apos;t
              take this page&apos;s word for anything — the verified source is linked below.
            </li>
            <li>
              <strong>The market sets the price.</strong> JDAI trades freely on PulseX and
              its market price can drift from the gold target. Compare both numbers above
              before you mint.
            </li>
          </ul>
        </div>
      </Alert>

      {/* --------------------------- Verify strip ------------------------- */}
      <div style={{ textAlign: 'center' }}>
        <SectionTitle style={{ marginBottom: '1rem' }}>Don&apos;t trust — verify</SectionTitle>
        <VerifyRow>
          <a href={`https://repo.sourcify.dev/369/${CONTRACTS.JDAI}`} target="_blank" rel="noopener noreferrer">
            JDAI token source ↗
          </a>
          <a href={`https://repo.sourcify.dev/369/${CONTRACTS.VAT}`} target="_blank" rel="noopener noreferrer">
            Vault engine source ↗
          </a>
          <a href="https://github.com/USCGVet/jdai-dapp-source" target="_blank" rel="noopener noreferrer">
            This site&apos;s code ↗
          </a>
          <a href={`https://dexscreener.com/pulsechain/${CONTRACTS.JDAI}`} target="_blank" rel="noopener noreferrer">
            Live market ↗
          </a>
        </VerifyRow>
        <p style={{ fontSize: '0.8rem', color: '#777', marginTop: '1rem' }}>
          Every figure on this page is read live from PulseChain in your browser over
          public RPCs. No server, no tracking, no wallet needed to look around.
        </p>
      </div>

      {/* ---------------------------- Bottom CTA --------------------------- */}
      <div style={{ textAlign: 'center', padding: '1rem 0 2rem' }}>
        <WalletButton onClick={connected ? onEnterVault : onConnect} disabled={isConnecting}>
          {connected ? 'Go to your vault →' : isConnecting ? 'Connecting...' : 'Open Your Vault'}
        </WalletButton>
      </div>
    </Landing>
  );
};

export default LandingPage;
